import "./style.css";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import homepodVertexShader from "../shaders/homepod/vertex.glsl";
import homepodFragmentShader from "../shaders/homepod/fragment.glsl";
import tvScreenVertexShader from "../shaders/tvScreen/vertex.glsl";
import tvScreenFragmentShader from "../shaders/tvScreen/fragment.glsl";
import smokeVertexShader from "../shaders/smoke/vertex.glsl";
import smokeFragmentShader from "../shaders/smoke/fragment.glsl";
import steamVertexShader from "../shaders/steam/vertex.glsl";
import steamFragmentShader from "../shaders/steam/fragment.glsl";

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x665c55);

/**
 * Loaders
 */
// Texture loader
const textureLoader = new THREE.TextureLoader();

// Draco loader
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("draco/");

// GLTF loader
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

// TEXTURES

const bakedTex = textureLoader.load("livingroom_baked.jpg");
bakedTex.flipY = false;
bakedTex.encoding = THREE.sRGBEncoding;

// MATERIALS

const bakedMat = new THREE.MeshBasicMaterial({
  map: bakedTex,
});

const lampMat = new THREE.MeshBasicMaterial({ color: 0xffffe5 });

const emberMat = new THREE.MeshBasicMaterial({ color: 0xff0000 });

const tvScreenMat = new THREE.ShaderMaterial({
  uniforms: {
    uTime: {
      time: 0,
    },
  },
  vertexShader: tvScreenVertexShader,
  fragmentShader: tvScreenFragmentShader,
});

const homepodMat = new THREE.ShaderMaterial({
  uniforms: {
    uTime: {
      time: { value: 0 },
    },
  },
  vertexShader: homepodVertexShader,
  fragmentShader: homepodFragmentShader,
});

const smokeMat = new THREE.ShaderMaterial({
  transparent: true,
  depthWrite: false,
  uniforms: {
    uTime: { value: 0 },
    uUvFrequency: { value: new THREE.Vector2(30.0, 12.0) },
    uColor: { value: new THREE.Color(0xffffff) },
  },
  vertexShader: smokeVertexShader,
  fragmentShader: smokeFragmentShader,
});

const steamMat = new THREE.ShaderMaterial({
  transparent: true,
  depthWrite: false,
  uniforms: {
    uTime: { value: 0 },
    uUvFrequency: { value: new THREE.Vector2(4.0, 3.0) },
    uColor: { value: new THREE.Color(0xc2b9a3) },
  },
  vertexShader: steamVertexShader,
  fragmentShader: steamFragmentShader,
});

// MODEL
gltfLoader.load("livingroom.glb", (gltf) => {
  gltf.scene.traverse((child) => {
    child.material = bakedMat;
  });
  scene.add(gltf.scene);

  const lampLight = gltf.scene.children.find((c) => c.name === "lampLight");
  lampLight.material = lampMat;

  const tvScreen = gltf.scene.children.find((c) => c.name === "tvScreen");
  tvScreen.material = tvScreenMat;

  const homepodScreen = gltf.scene.children.find(
    (c) => c.name === "homepodScreen"
  );
  homepodScreen.material = homepodMat;

  const ember = gltf.scene.children.find((c) => c.name === "ember");
  ember.material = emberMat;

  const smoke = gltf.scene.children.find((c) => c.name === "smoke");
  smoke.material = smokeMat;

  const steam = gltf.scene.children.find((c) => c.name === "steam");
  steam.material = steamMat;
});

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  45,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.x = 5;
camera.position.y = 1.5;
camera.position.z = 0;
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.minDistance = 10;
controls.maxDistance = 20;
controls.maxPolarAngle = 1.5;
controls.enableDamping = true;
controls.maxAzimuthAngle = Math.PI / 2;
controls.minAzimuthAngle = Math.PI * 2;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.outputEncoding = THREE.sRGBEncoding;

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
  const elapsedTime = clock.getElapsedTime();

  homepodMat.uniforms.uTime.value = elapsedTime;
  tvScreenMat.uniforms.uTime.value = elapsedTime;
  smokeMat.uniforms.uTime.value = elapsedTime;
  steamMat.uniforms.uTime.value = elapsedTime;

  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
